/**
 * PeopleAndLinesDrawer (360-RELOOK-01 R1, 2026-09-03) — ONE place for every
 * person and every line on an account, from every source, with the consent
 * that goes with each:
 *   · Pegasus contacts (name, role, phone, email — fully editable)
 *   · ESC's contact grid for the property (name · phone · email), or the
 *     labeled phone bank when the grid is empty
 *   · the customer-level rules ("whole account": ESC Disable Emails, office
 *     do-not-contact)
 * Every phone carries three text pills and every email three email pills —
 * Job updates · Invoices · Marketing — click to allow / unsubscribe. A texted
 * STOP is locked; only a texted START lifts it. Seth's rule: everything
 * allowed unless there is proof, and the proof is shown, not implied.
 * ONE exception (MKT M6): marketing TEXTS need an opt-in on record — the
 * Texts → Marketing pill is "no opt-in" until someone records a yes here
 * (office_optin, with who and when), the customer texts OFFERS, or the portal.
 */
import { useMemo, useState } from "react";
import { Phone, MessageSquare, Mail, Pencil, Plus, UserRound, Building2, Lock, ShieldCheck, ShieldOff, ChevronDown, ChevronUp, History } from "lucide-react";
import { cn } from "@/lib/cn";
import { SlideOver } from "@/components/ui/SlideOver";
import { useCan } from "@/lib/permissions/PermissionProvider";
import type { CustomerWithLocations, LocationRow } from "./api";
import { useContacts, ContactModal, type ContactRow } from "./CustomerContacts";
import { buildLines, splitLinesByEra, contactEraOf, type Line } from "./contactLines";
import { useLocationOwnership } from "@/features/jobs/properties";
// The line-building logic moved to ./contactLines (34i87); re-exported for the rail.
export { buildLines, type Line, type ContactEra, contactEraOf, splitLinesByEra } from "./contactLines";
import {
  useCustomerConsent, useSetConsent, consentReason, optinReason, isSmsMarketingOptin, PURPOSES,
  type ConsentChannel, type ConsentPurpose, type ConsentStatus, type ConsentRow,
} from "@/features/comms/consent";

type PillStatus = ConsentStatus | "not_opted_in";

const p10 = (s: string) => s.replace(/\D/g, "").slice(-10);
const prettyPhone = (s: string) => { const d = p10(s); return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : s; };

export function PeopleAndLinesDrawer({ open, onClose, customer, location, onText, onEmail }: {
  open: boolean;
  onClose: () => void;
  customer: CustomerWithLocations;
  location: LocationRow | null;
  onText?: (phone: string) => void;
  onEmail?: (email: string) => void;
}) {
  const custno = customer.custno;
  const locno = location?.locno ?? null;
  const contacts = useContacts(custno, locno).data ?? [];
  const canEditContacts = useCan("contacts.edit");
  const canEditConsent = useCan("comms.send");
  const [editing, setEditing] = useState<{ contact: ContactRow | null; prefill?: { name?: string; role?: string; phone?: string; email?: string } } | null>(null);
  // R2c (Seth: the drawer was "awkward and overcomplicated"): consent detail is
  // COLLAPSED behind one summary chip per line; account-wide rules sit at the
  // bottom, closed unless one is set.
  const [openConsent, setOpenConsent] = useState<Record<string, boolean>>({});
  const [rulesOpen, setRulesOpen] = useState(false);

  // NEW-OWNER-CONTACTS (34i87): after a recorded sale the previous owner's
  // lines leave the list; they stay reachable at the bottom.
  const { data: owned } = useLocationOwnership(custno, locno);
  const era = useMemo(() => contactEraOf(owned), [owned]);
  const split = useMemo(() => (era ? splitLinesByEra(customer, location, contacts, era) : null), [contacts, customer, location, era]);
  const lines = useMemo<Line[]>(() => split?.current ?? buildLines(customer, location, contacts), [split, contacts, customer, location]);
  const previousLines = split?.previous ?? [];
  const [prevOpen, setPrevOpen] = useState(false);

  const endpoints = useMemo(() => {
    const eps: { channel: ConsentChannel; endpoint: string }[] = [];
    for (const l of lines) {
      if (l.phone) eps.push({ channel: "sms", endpoint: l.phone });
      if (l.email) eps.push({ channel: "email", endpoint: l.email });
    }
    return eps;
  }, [lines]);
  const consent = useCustomerConsent(custno, endpoints);
  const setConsent = useSetConsent();

  const blocks = consent.rows.filter((r) => r.status !== "allowed").length;
  const people = lines.filter((l) => l.name).length;

  return (
    <SlideOver open={open} onClose={onClose} title="People & Lines" size="lg"
      subtitle={`${people} ${people === 1 ? "person" : "people"} · ${lines.length} line${lines.length === 1 ? "" : "s"}${blocks ? ` · ${blocks} consent block${blocks === 1 ? "" : "s"}` : " · everything allowed"}`}>
      <div className="space-y-4">
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">People and Lines on This Account</h3>
            {canEditContacts && (
              <button type="button" onClick={() => setEditing({ contact: null })} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline">
                <Plus className="h-3.5 w-3.5" /> Add a person
              </button>
            )}
          </div>
          {lines.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-400">{era?.owner ? `No lines for ${era.owner.name} yet — add the first.` : "No phone numbers or emails on file for this property."}</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-xl border border-hairline bg-white">
              {lines.map((l) => {
                const summary = consentSummary(l, consent);
                const expanded = !!openConsent[l.key];
                return (
                  <li key={l.key} className="px-3 py-2.5">
                    <div className="flex items-start gap-2.5">
                      <span className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full", l.origin === "pegasus" ? "bg-brand-50 text-brand-700" : "bg-slate-100 text-slate-500")}>
                        {l.origin === "pegasus" ? <UserRound className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <span className="text-sm font-semibold text-slate-900">{l.name ?? (l.role || "Line on file")}</span>
                          {l.name && l.role && <span className="text-xs text-slate-500">{l.role}</span>}
                          <span className="text-[10.5px] text-slate-400">{l.origin === "pegasus" ? (l.contact?.locno ? "Pegasus · this property" : "Pegasus · whole account") : l.origin === "esc-property" ? "from ESC · this property" : "from ESC · account"}</span>
                          {canEditContacts && (
                            <button type="button"
                              onClick={() => setEditing(l.contact ? { contact: l.contact } : { contact: null, prefill: { name: l.name ?? undefined, role: l.role ?? undefined, phone: l.phone ?? undefined, email: l.email ?? undefined } })}
                              title={l.contact ? "Edit this person" : "Edit in Pegasus — saves as a Pegasus contact (the ESC line stays as-is)"}
                              className="ml-auto rounded p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-700">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                          {l.phone && (
                            <span className="inline-flex items-center gap-1.5">
                              <a href={`tel:${p10(l.phone)}`} className="inline-flex items-center gap-1 text-[13px] font-medium text-slate-800 hover:underline"><Phone className="h-3.5 w-3.5 text-slate-400" /> {prettyPhone(l.phone)}{l.ext ? ` x${l.ext}` : ""}</a>
                              {onText && <button type="button" onClick={() => onText(l.phone!)} className="inline-flex items-center gap-1 rounded-md border border-hairline px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"><MessageSquare className="h-3 w-3" /> Text</button>}
                            </span>
                          )}
                          {l.email && (
                            <span className="inline-flex min-w-0 items-center gap-1.5">
                              <span className="inline-flex min-w-0 items-center gap-1 text-[13px] text-slate-800"><Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" /> <span className="truncate">{l.email}</span></span>
                              {onEmail && <button type="button" onClick={() => onEmail(l.email!)} className="inline-flex items-center gap-1 rounded-md border border-hairline px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"><Mail className="h-3 w-3" /> Email</button>}
                            </span>
                          )}
                          <button type="button" onClick={() => setOpenConsent((m) => ({ ...m, [l.key]: !expanded }))}
                            title="What Pegasus may send to this line, and for what"
                            className={cn("ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold", summary.tone === "stop" ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200" : summary.tone === "off" ? "bg-slate-100 text-slate-600 ring-1 ring-slate-200" : summary.tone === "optin" ? "border border-dashed border-slate-300 bg-white text-slate-500" : "bg-emerald-50 text-emerald-700")}>
                            {summary.tone === "stop" ? <Lock className="h-3 w-3" /> : summary.tone === "ok" ? <ShieldCheck className="h-3 w-3" /> : <ShieldOff className="h-3 w-3" />}
                            {summary.label}
                            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </button>
                        </div>
                        {expanded && (
                          <div className="mt-1.5 rounded-lg bg-slate-50/70 px-2 py-1">
                            {l.phone && (
                              <EndpointRow
                                icon={<MessageSquare className="h-3.5 w-3.5 text-slate-400" />}
                                label="Texts"
                                channel="sms" endpoint={l.phone} custno={custno} consent={consent} canEdit={canEditConsent}
                                onSet={(purpose, status, source) => setConsent.mutate({ custno, channel: "sms", endpoint: l.phone!, purpose, status, source, note: source === "office_optin" ? "customer said yes to marketing texts" : null })}
                              />
                            )}
                            {l.email && (
                              <EndpointRow
                                icon={<Mail className="h-3.5 w-3.5 text-slate-400" />}
                                label="Email"
                                channel="email" endpoint={l.email} custno={custno} consent={consent} canEdit={canEditConsent}
                                onSet={(purpose, status) => setConsent.mutate({ custno, channel: "email", endpoint: l.email!, purpose, status })}
                              />
                            )}
                            <p className="mt-1 text-[10.5px] leading-snug text-slate-400">Green = allowed, grey = unsubscribed (click to change), red lock = texted STOP (only a texted START lifts it). Marketing texts need a yes on record — "no opt-in" until you record one or the customer texts OFFERS.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {era && previousLines.length > 0 && (
          <section className="rounded-xl border border-hairline bg-white">
            <button type="button" onClick={() => setPrevOpen((v) => !v)} className="flex w-full items-center gap-2 px-3 py-2 text-left">
              <History className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Previous owner</span>
              <span className="text-[11px] text-slate-400">{previousLines.length} line{previousLines.length === 1 ? "" : "s"} ESC still carries</span>
              {prevOpen ? <ChevronUp className="ml-auto h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="ml-auto h-3.5 w-3.5 text-slate-400" />}
            </button>
            {prevOpen && (
              <ul className="divide-y divide-slate-100 border-t border-slate-100">
                {previousLines.map((l) => (
                  <li key={l.key} className="flex flex-wrap items-baseline gap-x-2 px-3 py-1.5 text-xs text-slate-500">
                    {l.name && <span className="font-medium text-slate-700">{l.name}</span>}
                    {l.role && <span className="text-slate-400">{l.role}</span>}
                    {l.phone && <span className="tabular-nums">{prettyPhone(l.phone)}{l.ext ? ` x${l.ext}` : ""}</span>}
                    {l.email && <span className="truncate">{l.email}</span>}
                    {canEditContacts && (
                      <button type="button" onClick={() => setEditing({ contact: null, prefill: { name: l.name ?? undefined, role: l.role ?? undefined, phone: l.phone ?? undefined, email: l.email ?? undefined } })} className="ml-auto text-[11px] font-medium text-brand-600 hover:underline" title="This line belongs to the current owner after all — save it as a Pegasus contact">
                        Keep
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="rounded-xl border border-hairline bg-white">
          <button type="button" onClick={() => setRulesOpen((v) => !v)} className="flex w-full items-center gap-2 px-3 py-2 text-left">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Account-wide rules</span>
            <span className="text-[11px] text-slate-400">{accountRuleSummary(custno, consent)}</span>
            {rulesOpen ? <ChevronUp className="ml-auto h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="ml-auto h-3.5 w-3.5 text-slate-400" />}
          </button>
          {rulesOpen && (
            <div className="border-t border-slate-100 px-3 pb-2.5">
              <AccountRules custno={custno} consent={consent} canEdit={canEditConsent} onSet={(channel, purpose, status, source) =>
                setConsent.mutate({ custno, channel, endpoint: "*", purpose, status, source: source ?? "office_pref", note: source === "office_optin" ? "customer said yes to marketing texts (whole account)" : null })} />
              <p className="mt-1 text-[10.5px] text-slate-400">A rule here applies to every line on the account unless a line says otherwise (ESC's Disable Emails lands here).</p>
            </div>
          )}
        </section>
      </div>

      {editing && (
        <ContactModal custno={custno} locno={locno} contact={editing.contact} prefill={editing.prefill} onClose={() => setEditing(null)} />
      )}
    </SlideOver>
  );
}

/** One chip per line: the worst thing on it, or "OK to text & email". */
function consentSummary(l: Line, consent: ReturnType<typeof useCustomerConsent>): { label: string; tone: "ok" | "off" | "stop" | "optin" } {
  const rows: ConsentRow[] = [];
  for (const p of PURPOSES) {
    if (l.phone) { const r = consent.rowFor("sms", l.phone, p.key); if (r) rows.push(r); }
    if (l.email) { const r = consent.rowFor("email", l.email, p.key); if (r) rows.push(r); }
  }
  if (rows.some((r) => r.status === "stop")) return { label: "texted STOP", tone: "stop" };
  const off = rows.filter((r) => r.status === "unsubscribed");
  if (off.length) {
    const jobs = off.some((r) => r.purpose === "job_updates");
    const chans = [...new Set(off.map((r) => (r.channel === "sms" ? "texts" : "email")))].join(" & ");
    return { label: jobs ? `no ${chans}` : `no marketing ${chans}`, tone: "off" };
  }
  // The marketing-text opt-in (rare today) stays inside the expanded pills — it
  // is not worth a chip on every line.
  return { label: l.phone && l.email ? "OK to text & email" : l.phone ? "OK to text" : "OK to email", tone: "ok" };
}
function accountRuleSummary(custno: string, consent: ReturnType<typeof useCustomerConsent>): string {
  const rules = consent.rows.filter((r) => r.custno === custno && r.endpoint === "*" && r.status !== "allowed");
  if (rules.length === 0) return "none set";
  return [...new Set(rules.map((r) => consentReason(r)).filter(Boolean))].join(" · ");
}

/** Whole-account rules: ESC's Disable Emails, office do-not-contact. */
function AccountRules({ custno, consent, canEdit, onSet }: {
  custno: string;
  consent: ReturnType<typeof useCustomerConsent>;
  canEdit: boolean;
  onSet: (channel: ConsentChannel, purpose: ConsentPurpose, status: ConsentStatus, source?: string) => void;
}) {
  const rules = consent.rows.filter((r) => r.custno === custno && r.endpoint === "*");
  return (
    <section className="pt-2">
      <div className="grid gap-1.5 sm:grid-cols-2">
        {(["email", "sms"] as ConsentChannel[]).map((ch) => (
          <div key={ch} className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="w-12 font-medium text-slate-600">{ch === "sms" ? "Texts" : "Email"}</span>
            {PURPOSES.map((p) => {
              const row = rules.find((r) => r.channel === ch && r.purpose === p.key) ?? null;
              const optin = ch === "sms" && p.key === "marketing";
              const status: PillStatus = optin ? smsMarketingStatus(row) : (row?.status ?? "allowed");
              return (
                <Pill key={p.key} label={p.short} status={status} reason={optin ? (optinReason(row) ?? consentReason(row)) : consentReason(row)} canEdit={canEdit}
                  onToggle={() => onSet(ch, p.key, ...nextFor(status, optin))} />
              );
            })}
          </div>
        ))}
      </div>
      {rules.some((r) => r.status !== "allowed") && (
        <p className="mt-1.5 text-[11px] text-slate-500">
          {[...new Set(rules.filter((r) => r.status !== "allowed").map((r) => consentReason(r)).filter(Boolean))].join(" · ")}
        </p>
      )}
    </section>
  );
}

function EndpointRow({ icon, label, channel, endpoint, custno, consent, canEdit, onSet }: {
  icon: React.ReactNode;
  label: string;
  channel: ConsentChannel;
  endpoint: string;
  custno: string;
  consent: ReturnType<typeof useCustomerConsent>;
  canEdit: boolean;
  onSet: (purpose: ConsentPurpose, status: ConsentStatus, source?: string) => void;
}) {
  void custno;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
      {icon}
      <span className="w-10 text-[12px] font-medium text-slate-600">{label}</span>
      <span className="ml-auto flex flex-wrap items-center gap-1">
        {PURPOSES.map((p) => {
          const row = consent.rowFor(channel, endpoint, p.key);
          const optin = channel === "sms" && p.key === "marketing";
          const status: PillStatus = optin ? smsMarketingStatus(row) : (row?.status ?? "allowed");
          return (
            <Pill key={p.key} label={p.short} status={status} reason={optin ? (optinReason(row) ?? consentReason(row)) : consentReason(row)} canEdit={canEdit}
              inherited={!!row && row.endpoint === "*"}
              onToggle={() => onSet(p.key, ...nextFor(status, optin))} />
          );
        })}
      </span>
    </div>
  );
}

/** MKT M6: the Texts → Marketing pill has a third state — "no opt-in" (nothing on record, or only a texted START). */
function smsMarketingStatus(row: ConsentRow | null): PillStatus {
  if (!row) return "not_opted_in";
  if (row.status === "allowed") return isSmsMarketingOptin(row) ? "allowed" : "not_opted_in";
  return row.status;
}
/** What a click does: opt in (recorded, with who/when) ⇄ unsubscribe. */
function nextFor(status: PillStatus, optin: boolean): [ConsentStatus, string | undefined] {
  if (!optin) return [status === "allowed" ? "unsubscribed" : "allowed", undefined];
  return status === "allowed" ? ["unsubscribed", "office_pref"] : ["allowed", "office_optin"];
}

function Pill({ label, status, reason, canEdit, inherited, onToggle }: {
  label: string; status: PillStatus; reason: string | null; canEdit: boolean; inherited?: boolean; onToggle: () => void;
}) {
  const locked = status === "stop";
  const title = locked ? (reason ?? "texted STOP")
    : status === "unsubscribed" ? `${reason ?? "unsubscribed"}${inherited ? " (whole-account rule)" : ""} — click to allow`
    : status === "not_opted_in" ? "No marketing-text opt-in on record — click to record that the customer said yes (who and when are kept)"
    : `${reason ?? "Allowed"} — click to unsubscribe`;
  return (
    <button type="button" disabled={locked || !canEdit} onClick={onToggle} title={title}
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10.5px] font-semibold transition-colors",
        status === "allowed" && "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100",
        status === "unsubscribed" && "bg-slate-100 text-slate-500 ring-1 ring-slate-200 hover:bg-slate-200",
        status === "not_opted_in" && "border border-dashed border-slate-300 bg-white text-slate-400 hover:bg-slate-50 hover:text-slate-600",
        locked && "bg-rose-50 text-rose-700 ring-1 ring-rose-200 cursor-not-allowed",
        !canEdit && !locked && "cursor-default",
      )}>
      {locked ? <Lock className="h-3 w-3" /> : status === "allowed" ? <ShieldCheck className="h-3 w-3" /> : <ShieldOff className="h-3 w-3" />}
      {label}{status === "not_opted_in" ? " · no opt-in" : ""}
    </button>
  );
}
